import React from 'react';
import { Award, GraduationCap, BadgeCheck, Stethoscope } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useAppointment } from '../context/AppointmentContext';
import useJsonLd from '../hooks/useJsonLd';
import usePageSeo from '../hooks/usePageSeo';

const doctors = [
  {
    id: 1,
    name: "Dr. Carroll D. Cooper",
    title: "Interventional Musculoskeletal Physiatrist",
    image: "/webp-images/dr-carroll-cooper.webp",
    imagePosition: "object-top",
    bio: "A native of Huntsville, Alabama, Dr. Carroll D. Cooper moved to Atlanta to attend Morehouse College. Following his time at Morehouse, Dr. Cooper obtained his medical degree from the University of Alabama-Birmingham School of Medicine.\n\nHe did his residency training, in Physical Medicine and Rehabilitation, at the Medical College of Wisconsin. After finishing residency, he underwent additional training at The Hospital for Special Surgery, completing a fellowship in Interventional Spine and Sports Medicine.\n\nDr. Cooper focuses on regenerative treatments for non-surgical orthopedic problems. Utilizing platelet rich plasma, stem cells, and traditional corticosteroids, Dr. Cooper provides patients with non-surgical options for common orthopedic problems.",
    certifications: [
      "Board Certified in Physical Medicine and Rehabilitation",
      "Board Certified for Electrodiagnostic Medicine"
    ],
    education: [
      "B.S. in Chemistry from Morehouse College",
      "M.D. from the University of Alabama School of Medicine",
      "Residency in Physical Medicine & Rehabilitation at Medical College of Wisconsin",
      "Fellowship in Interventional Spine & Sports Medicine from the Hospital for Special Surgery in New York City"
    ],
    accolades: [
      "NASA Scholar at Morehouse College",
      "Served as Chief Resident at the Medical College of Wisconsin",
      "Completed fellowship in Interventional Spine & Sports Medicine from the Hospital for Special Surgery in New York City – the premiere orthopedic hospital in the world",
      "Served as a team doctor for St. Peter's College in New Jersey"
    ]
  },
  {
    id: 2,
    name: "Dr. Pickens Patterson, III",
    title: "Pain Management & Anesthesiology Specialist",
    image: "/webp-images/Dr-Pickens-Patterson.webp",
    imagePosition: "object-[center_20%]",
    bio: "A native born Georgian, who grew up in Southwest Atlanta, Dr. Patterson takes pride in serving his community and the people of Georgia. Dr. Patterson has over 15 years of experience practicing pain management and anesthesiology. Additionally, Dr. Patterson is trained in advanced pain techniques such as spinal cord stimulation and implantable devices.",
    certifications: [
      "Board Certified in Anesthesiology",
      "Board Certified in Pain Management"
    ],
    education: [
      "Medical Degree from the Medical College of Georgia",
      "Residency in Anesthesiology and Fellowship in Pain Management at Vanderbilt University"
    ],
    accolades: [
      "Medical Association of Georgia member",
      "Georgia Society of Interventional Pain Management member",
      "Selected to be a part of the development of physician leaders in the state of Georgia sponsored by the Medical Association of Georgia"
    ]
  },
  // COMMENTED OUT - Christy Dickerson profile (hidden for now, re-enable if needed)
  // {
  //   id: 3,
  //   name: "Christy Dickerson",
  //   title: "AGNP-C",
  //   image: "https://atlantaregenerativemedicine.com/wp-content/uploads/2023/12/IMG-5447.jpg",
  //   imagePosition: "object-[center_15%]",
  //   bio: "A native of LaGrange, Georgia, Christy Dickerson, attended Clayton State University and Georgia State University. She completed Piedmont Atlanta Hospital nursing residency program with Orthopedic and Spine training. Christy promotes orthopedic education and focuses on musculoskeletal problems while providing non-surgical treatment options.",
  //   certifications: [
  //     "Board certified with American Academy of Nurse Practitioners",
  //     "Joint/Extremity/Non-spinal Injection Certification",
  //     "National Association of Orthopaedic Nurses Certificate"
  //   ],
  //   education: [
  //     "Bachelor of Science in Nursing from Clayton State University",
  //     "Master of Science in Nursing/Adult-Gerontology Nurse Practitioner from Georgia State University"
  //   ],
  //   accolades: [
  //     "Golden Key International Honour Society Member at Clayton State University",
  //     "Served as the Orthopedic/Spine unit Clinical Manager"
  //   ]
  // }
];

const DoctorCard = ({ doctor, isReversed }) => {
  return (
    <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-16 items-start`}>
      {/* Image */}
      <div className="w-full lg:w-[380px] flex-shrink-0 mx-auto lg:mx-0">
        <div className="relative group">
          <div className="absolute -inset-3 bg-gradient-to-br from-[#20B2AA]/20 to-[#E63946]/20 rounded-2xl blur-lg"></div>
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <img 
              src={doctor.image} 
              alt={doctor.name}
              className="w-full h-[420px] object-cover transform group-hover:scale-105 transition-transform duration-500"
              style={{ 
                filter: 'contrast(1.05) brightness(1.02)',
                objectPosition: doctor.id === 1 ? 'center 15%' : doctor.id === 2 ? 'center 25%' : 'center 20%'
              }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full lg:flex-1">
        <h2 className="text-3xl md:text-4xl font-bold text-[#1B1B3A] mb-2">{doctor.name}</h2>
        <p className="text-[#20B2AA] font-semibold text-lg mb-4">{doctor.title}</p>
        
        {/* Bio */}
        <div className="text-gray-600 leading-relaxed mb-6 whitespace-pre-line text-sm md:text-base">
          {doctor.bio}
        </div>

        {/* Credentials Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Certifications */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#E63946]/10 p-2 rounded-lg">
                <BadgeCheck className="h-5 w-5 text-[#E63946]" />
              </div>
              <h3 className="font-bold text-[#1B1B3A]">Certifications</h3>
            </div>
            <ul className="space-y-2">
              {doctor.certifications.map((cert, index) => (
                <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="text-[#20B2AA] mt-1">•</span>
                  {cert}
                </li>
              ))}
            </ul>
          </div>

          {/* Education */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#20B2AA]/10 p-2 rounded-lg">
                <GraduationCap className="h-5 w-5 text-[#20B2AA]" />
              </div>
              <h3 className="font-bold text-[#1B1B3A]">Education</h3>
            </div>
            <ul className="space-y-2">
              {doctor.education.map((edu, index) => (
                <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="text-[#20B2AA] mt-1">•</span>
                  {edu}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Accolades */}
        <div className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] rounded-xl p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white/10 p-2 rounded-lg">
              <Award className="h-5 w-5 text-[#20B2AA]" />
            </div>
            <h3 className="font-bold text-white">Accolades</h3>
          </div>
          <ul className="space-y-2">
            {doctor.accolades.map((accolade, index) => (
              <li key={index} className="text-sm text-gray-300 flex items-start gap-2">
                <span className="text-[#20B2AA] mt-1">•</span>
                {accolade}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const drCooperSchema = {
  "@context": "https://schema.org",
  "@type": "Physician",
  "name": "Dr. Carroll D. Cooper",
  "image": "https://www.atlregen.com/webp-images/dr-carroll-cooper.webp",
  "jobTitle": "Interventional Musculoskeletal Physiatrist",
  "medicalSpecialty": ["PhysicalMedicine", "Musculoskeletal"],
  "worksFor": {
    "@type": "MedicalClinic",
    "name": "ATL Regen",
    "url": "https://www.atlregen.com/"
  },
  "alumniOf": [
    { "@type": "CollegeOrUniversity", "name": "Morehouse College" },
    { "@type": "CollegeOrUniversity", "name": "University of Alabama School of Medicine" }
  ],
  "sameAs": [
    "https://www.linkedin.com/in/carroll-cooper-10b3025"
  ]
};

const MeetTheDoctor = () => {
  const { openAppointmentModal } = useAppointment();

  usePageSeo({
    title: "Meet Our Doctors | Dr. Cooper & Dr. Patterson | ATL Regen",
    description: "Meet Dr. Carroll D. Cooper and Dr. Pickens Patterson III, board-certified regenerative medicine specialists serving patients in Atlanta, GA.",
    canonical: "https://www.atlregen.com/meet-the-doctor",
    ogTitle: "Meet Our Doctors | Board-Certified Regenerative Medicine Specialists",
  });

  useJsonLd(drCooperSchema);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
                <Stethoscope className="h-5 w-5 text-[#20B2AA]" />
                <span className="text-sm font-medium">Our Medical Team</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Meet the Doctors</h1>
              <p className="text-xl text-gray-300">
                Founded in 2011 with a focus on Regenerative Spinal Medicine.
              </p>
            </div>
          </div>
        </section>

        {/* Doctors Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto space-y-20">
              {doctors.map((doctor, index) => (
                <div key={doctor.id} data-testid={`doctor-card-${doctor.id}`}>
                  <DoctorCard doctor={doctor} isReversed={index % 2 !== 0} />
                  {index < doctors.length - 1 && (
                    <div className="border-b border-gray-200 mt-20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#E63946] to-[#c62d3a]">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey to Recovery?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our experienced medical team today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+14043552728"
                className="bg-white text-[#E63946] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                data-testid="meet-doctor-call-btn"
              >
                Call (404) 355-2728
              </a>
              <button 
                onClick={openAppointmentModal}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-colors"
                data-testid="meet-doctor-schedule-btn"
              >
                Schedule Consultation
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MeetTheDoctor;
